<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { range } from "underscore";
import { useRoute, useRouter } from "vue-router";

import { PANEL_SIZE } from "../../constants";

import ConfirmPopover from "@/components/ConfirmPopover.vue";

import { useIsPopout } from "@/hooks/use-is-popout";

import { useAnnotationStore } from "@/stores/annotation-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

import { deleteAnnotation, markDefaults, saveAnnotation, isRedaction } from "@/services/AnnotationService";
import {
  getAnnotationMeta,
  getMarkStatus,
  getMarkEvidences,
  isRectangleAnnotation,
  isHighlightAnnotation,
} from "@/services/UtilitiesService";

import type { MarkBase } from "@/models/MarkBase";
import type { Metadata } from "@/models/Metadata";
import type { HighlightAnnotation, RectangleAnnotation } from "@/models/Annotation";
import type { ParsedMark } from "@/models/Mark";

const emit = defineEmits<(e: "reloadAnnotations") => void>();
withDefaults(defineProps<{
  file: Metadata;
  snippets: MarkBase[];
}>(), {  })

const $route = useRoute();
const $router = useRouter();

const isPopout = useIsPopout();

const colors = [
  "#ffff00",
  "#ff0000",
  "#f7941d",
  "#00ff00",
  "#00ffff",
  "#ff00ff",
  "#6666ff",
  "#cccccc",
  "#ffffff",
  "#cc0000",
  "#999900",
  "#0000ff",
  "#3333cc",
  "#888888",
  "#000000",
];
let dropdownShown = _ref<string | null>(null);

const canRedact = _computed(() => {
  return useUserPermissionStore().userPermission.canRedact;
});

const canAnnotate = _computed(() => {
  return useUserPermissionStore().userPermission.canAnnotate || false;
});

const params = _computed(() => {
  return $route.params;
});

const selectedMark = _computed(() => {
  return sortedMarks.value.find((a) => a.uid === params.value.annotation);
});

const annotations = _computed(() => {
  return useAnnotationStore().annotations.filter(
    (a): a is ParsedMark<HighlightAnnotation | RectangleAnnotation> =>
      a.fileReference === __props.file.fileReference &&
      (isRectangleAnnotation(a.parsedMarkData) || isHighlightAnnotation(a.parsedMarkData)),
  );
});

const sortedMarks = _computed(() => {
  return annotations.value
    .flatMap((a) => {
      const status = getMarkStatus(a);
      if (!/user|accepted|candidate/i.test(status)) return [];

      let markData = a.parsedMarkData;

      let thickness = 6;

      if (markData.type === "LineAnnotation") {
        markData = { ...markData, fillColor: (markData as unknown as { color: string; thickness: number }).color };
        thickness = (markData as unknown as { color: string; thickness: number }).thickness;
      }

      return {
        ...markData,
        data: { ...markData.data, status },
        thickness,
        ...(status !== "user" ? markDefaults[status] || markDefaults.ai : {}),
      };
    })
    .sort((a, b) => {
      const { rect: recta } = getAnnotationMeta(a);
      const { rect: rectb } = getAnnotationMeta(b);
      return a.pageNumber - b.pageNumber || recta.y - rectb.y || recta.x - rectb.x;
    });
});

async function saveMark(value: string | number, type: "opacity" | "color" | "thickness") {
  const annotation = annotations.value.find((a) => a.markId === params.value.annotation);
  if (annotation?.parsedMarkData == null) return;

  await saveAnnotation({
    ...annotation,
    markData: JSON.stringify({
      ...annotation.parsedMarkData,
      ...(type === "color" ? { color: value, fillColor: value } : { [type]: value }),
    }),
  });

  dropdownShown.value = null;
  emit("reloadAnnotations");
}

async function deleteMark(markId: string) {
  const annotation = annotations.value.find((a) => markId === a.markId);
  if (!annotation) return;

  const { brand, casename, casefile } = $route.params;
  $router.push({ name: "casefile", params: { brand, casename, casefile } });

  await deleteAnnotation(annotation);
}

function hideDropdown() {
  dropdownShown.value = null;
}

function toggleDropdown(type: string, disabled: boolean) {
  if (!selectedMark.value || disabled) {
    return;
  }
  dropdownShown.value = dropdownShown.value !== type ? type : null;
}

function selectMark(markId: string) {
  if (isPopout) return;
  const { brand, casename, casefile } = $route.params;

  $router.push({
    name: "casefileannotation",
    params: { brand, casename, casefile, annotation: markId },
  });
}
</script>
<template>
  <div :style="{ width: PANEL_SIZE + 'px' }" class="annotations-panel tw-shadow">
    <div v-click-away="hideDropdown" class="dropdown-container">
      <div class="tw-mb-2 tw-flex">
        <div
          class="dropdown-trigger tw-mr-2 tw-flex"
          :class="{
            disabled:
              !selectedMark || !canAnnotate || isRedaction(selectedMark) || !/user/i.test(selectedMark.data.status),
          }"
          @click="
            toggleDropdown(
              'colors',
              !selectedMark || !canAnnotate || isRedaction(selectedMark) || !/user/i.test(selectedMark.data.status),
            )
          "
        >
          <div
            class="color tw-flex-shrink-0"
            :style="{
              background: selectedMark ? selectedMark.fillColor : colors[0],
            }"
          ></div>
          <i class="fas fa-sort-down" />
        </div>
        <div
          class="dropdown-trigger tw-mr-2 tw-flex"
          :class="{
            disabled:
              !selectedMark ||
              selectedMark.type === 'HighlightAnnotation' ||
              !canAnnotate ||
              isRedaction(selectedMark) ||
              !/user/i.test(selectedMark.data.status),
          }"
          @click="
            toggleDropdown(
              'opacity',
              !selectedMark ||
                selectedMark.type === 'HighlightAnnotation' ||
                !canAnnotate ||
                isRedaction(selectedMark) ||
                !/user/i.test(selectedMark.data.status),
            )
          "
        >
          <div class="line tw-flex-shrink-0">
            <span
              class="box-opacity"
              :style="{
                opacity: selectedMark && selectedMark.opacity ? Math.round(selectedMark.opacity / 25.5) / 10 : 0.5,
              }"
            ></span>
            {{ selectedMark && selectedMark.opacity ? Math.round(selectedMark.opacity / 25.5) * 10 : 50 }}%
          </div>
          <i class="fas fa-sort-down" />
        </div>
        <div
          class="dropdown-trigger tw-mr-2 tw-flex"
          :class="{
            disabled:
              !selectedMark ||
              selectedMark.type !== 'LineAnnotation' ||
              !canAnnotate ||
              !/user/i.test(selectedMark.data.status),
          }"
          @click="
            toggleDropdown(
              'weights',
              !selectedMark ||
                selectedMark.type !== 'LineAnnotation' ||
                !canAnnotate ||
                !/user/i.test(selectedMark.data.status),
            )
          "
        >
          <div class="line tw-flex-shrink-0">
            <span class="line-weight" :style="{ height: (selectedMark?.thickness ?? 6) + 'px' }"></span>
            {{ selectedMark?.thickness ?? 6 }}
          </div>
          <i class="fas fa-sort-down" />
        </div>
      </div>
      <div v-show="dropdownShown === 'colors'" v-if="selectedMark" class="dropdown-list colors">
        <div
          v-for="color in colors"
          :key="color"
          class="color"
          :style="{ background: color }"
          @click="saveMark(color, 'color')"
        ></div>
      </div>
      <div v-show="dropdownShown === 'opacity'" v-if="selectedMark" class="dropdown-list box-opacities">
        <div v-for="o in range(10)" :key="o" class="line" @click="saveMark(o * 25.5, 'opacity')">
          <span class="box-opacity" :style="{ opacity: o / 10 }"></span>
          {{ o * 10 }}%
        </div>
      </div>
      <div v-show="dropdownShown === 'weights'" v-if="selectedMark" class="dropdown-list line-weights">
        <div v-for="h in range(1, 11)" :key="h" class="line" @click="saveMark(h, 'thickness')">
          <span class="line-weight" :style="{ height: h + 'px' }"></span>
          {{ h }}
        </div>
      </div>
    </div>
    <div v-if="sortedMarks.length" class="annotations">
      <div
        v-for="(mark, index) in sortedMarks"
        :key="mark.uid"
        class="annotation"
        :class="{
          odd: index % 2 === 1,
          selected: selectedMark && selectedMark.uid === mark.uid,
        }"
        @click="selectMark(mark.uid)"
      >
        <div class="tw-flex tw-items-center tw-gap-2">
          <div
            class="color"
            :style="{
              background: isRedaction(mark) ? '#000' : mark.fillColor,
            }"
          ></div>
          <i v-if="mark.type === 'RectangleAnnotation'" class="far fa-square" />
          <i v-else-if="mark.type === 'LineAnnotation'" class="fas fa-long-arrow-alt-up" />
          <i v-else-if="mark.type === 'HighlightAnnotation'" class="fas fa-highlighter" />
          <i v-else-if="mark.type === 'TextSelectionRedaction'" class="fas fa-eraser" />
          <span v-else-if="mark.type === 'RectangleRedaction'" class="fa-stack rotate-page">
            <i class="far fa-square fa-stack-2x tw-m-0 tw-p-0" />
            <i class="fas fa-eraser fa-stack-1x tw-m-0 tw-p-0" />
          </span>
          <div class="tw-flex-grow"></div>
          <template v-for="evidencesCount in [getMarkEvidences(mark).length]" :key="evidencesCount">
            <span v-if="!isRedaction(mark)" class="tw-ml-1 tw-flex tw-items-center tw-gap-0.5">
              <i class="fa-bookmark" :class="evidencesCount ? 'fas' : 'far'" />
              {{ evidencesCount }}
            </span>
          </template>
          <ConfirmPopover
            v-if="/user/i.test(mark.data.status) && canAnnotate && (!isRedaction(mark) || canRedact)"
            confirm-text="Delete"
            @confirmed="deleteMark(mark.uid)"
          >
            <template #trigger>
              <i class="fa fa-trash"></i>
            </template>
            <template #question>
              <template v-if="getMarkEvidences(mark).length > 0">
                This annotation has associated bookmarks. Are you sure you'd like to delete this annotation and all its
                associated bookmarks?
              </template>
              <template v-else>Are you sure you'd like to delete this annotation?</template>
            </template>
          </ConfirmPopover>
        </div>
      </div>
    </div>
    <div v-if="!sortedMarks.length">No annotations created</div>
  </div>
</template>
<style lang="scss" scoped>
.bottom-5 {
  bottom: 5px;
}
.right-5 {
  right: 5px;
}
.annotations-panel {
  padding: 12px 8px 2px 8px;
  display: flex;
  flex-direction: column;
}
.color {
  background: red;
  border: 1px inset #c2c5ce;
  border-radius: 2px;
  box-sizing: border-box;
  cursor: pointer;
  height: 18px;
  width: 18px;
  margin: 2px;
  float: left;
}
.line {
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  line-height: 26px;
  overflow: hidden;
  padding: 0 0 0 4px;
  text-overflow: ellipsis;
  white-space: nowrap;

  .box-opacity {
    background-color: #383a3f;
    box-sizing: border-box;
    display: inline-block;
    height: 14px;
    margin: -3px 2px 0 0;
    opacity: 0.2;
    vertical-align: middle;
    width: 14px;
  }
  .line-weight {
    background-color: #383a3f;
    box-sizing: border-box;
    display: inline-block;
    height: 3px;
    margin: -3px 7px 0 0;
    vertical-align: middle;
    width: 16px;
  }
}
.dropdown-trigger {
  background-color: #f2f3f6;
  border: 1px solid #c2c5ce;
  border-radius: 2px;
  box-sizing: border-box;
  height: 26px;
  line-height: 26px;
  outline: none;
  overflow: hidden;
  padding: 1px 11px 0 0;
  position: relative;
  text-indent: 3px;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;

  i {
    margin-top: 3px;
    cursor: pointer;
  }
}
.dropdown-container {
  position: relative;

  .disabled,
  .disabled * {
    opacity: 0.65;
    cursor: not-allowed;
  }
}
.dropdown-list {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #c2c5ce;
  box-shadow: rgba(0, 0, 0, 0.3) 0 2px 2px 0;
  overflow: auto;
  position: absolute;
  left: 0;
  top: 30px;
  transition-delay: 0s;
  transition-duration: 0.2s;
  transition-property: height;
  transition-timing-function: ease;
  visibility: visible;
  z-index: 3;

  &.colors {
    height: 48px;
    width: 225px;
    left: 0;
  }

  &.box-opacities {
    width: 78px;
    height: 200px;
    left: 54px;
  }
  &.line-weights {
    width: 78px;
    height: 200px;
    left: 134px;
  }
}

.annotations {
  border: 1px solid #dae1e7;
  background: #f2f3f6;
  overflow: auto;
  min-height: 0;

  .annotation {
    padding: 2px 4px;
    cursor: pointer;

    .color {
      float: none;
    }

    &.odd {
      background: white;
    }
    &.selected {
      background: #d3def4;
    }
  }
}

.comments {
  border: 1px solid #dae1e7;

  .comment {
    padding: 2px 4px;
    background: #f2f3f6;

    &.odd {
      background: white;
    }

    .comment-time {
      font-weight: 300;
      font-size: 12px;
    }
  }
}
</style>
